import React, { useState, useEffect } from "react";
import useDarkMode from '../../../hooks/useDarkMode';
import useThemeColor from "../../../hooks/useThemeColor";
import { useSelector } from "react-redux";
import axiosInstance from "../../../config/axiosApi/axiosApi";
import APIs from "../../../Constants/apis";
import CustomModal from "./customModal";
import { LazyLoadImage } from "react-lazy-load-image-component";

const SupplierProductSelectionModal = ({
  showModal,
  setShowModal,
  onSubmit,
  currentSelectedProducts = [],
}) => {
  const isOpen = showModal; // Map showModal to isOpen for CustomModal
  const [searchTerm, setSearchTerm] = useState("");
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [tempSelectedProducts, setTempSelectedProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const isDarkMode = useDarkMode();
  const themeColor = useThemeColor();
  const CompID = useSelector((state) => state.auth.user)?.userData?.compID;

  // Fetch all products on modal open
  useEffect(() => {
    if (showModal && products.length === 0) {
      fetchProducts();
    }
  }, [showModal]);

  // Initialize temp selected products when modal opens
  useEffect(() => {
    if (showModal) {
      setTempSelectedProducts([...currentSelectedProducts]);
    }
  }, [showModal, currentSelectedProducts]);

  // Filter products based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(products);
    } else {
      const filtered = products.filter(product =>
        product.menuItem_Name?.toLowerCase().includes(searchTerm.trim().toLowerCase())
      );
      setFilteredProducts(filtered);
    }
  }, [searchTerm, products]);

  const fetchProducts = async () => {
    if (!CompID) return;

    try {
      setIsLoading(true);
      const response = await axiosInstance.get(APIs.GET_ALL_ONLY_PRODUCTS, {
        params: { CompID },
      });

      const productData = response.data.data || response.data || [];
      if (Array.isArray(productData)) {
        setProducts(productData);
        setFilteredProducts(productData);
      } else {
        setProducts([]);
        setFilteredProducts([]);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
      setProducts([]);
      setFilteredProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (typeof onSubmit === "function") {
      // Transform selected products to include default price and preferred settings
      const productsWithDefaults = tempSelectedProducts.map(product => ({
        ...product,
        Supplier_Price: product.Supplier_Price || "",
        Is_Preferred: product.Is_Preferred || false,
      }));

      try {
        const result = await onSubmit(productsWithDefaults);
        if (result !== false) { // Only close modal if save was successful or no save function
          setShowModal(false);
        }
      } catch (error) {
        console.error("Error submitting modal:", error);
        // Don't close modal on error
      }
    } else {
      setShowModal(false);
    }
  };

  const handleProductSelection = (product) => {
    setTempSelectedProducts(prev => {
      const isSelected = prev.some(p => p.menuItem_ID === product.menuItem_ID);
      if (isSelected) {
        // Remove from selection
        return prev.filter(p => p.menuItem_ID !== product.menuItem_ID);
      } else {
        // Add to selection
        return [...prev, product];
      }
    });
  };

  const RenderProductCard = ({ product }) => {
    const isSelected = tempSelectedProducts.some(p => p.menuItem_ID === product.menuItem_ID);

    return (
      <div
        className="product-card"
        style={{
          background: isSelected
            ? (isDarkMode ? `${themeColor}20` : `${themeColor}15`)
            : (isDarkMode ? '#2d3748' : '#ffffff'),
          borderRadius: '12px',
          padding: '16px 20px',
          border: isSelected
            ? `2px solid ${themeColor}`
            : `2px solid ${isDarkMode ? '#4a5568' : '#e9ecef'}`,
          transition: 'all 0.2s ease',
          cursor: 'pointer',
          position: 'relative',
          boxShadow: isSelected
            ? `0 4px 12px ${themeColor}40`
            : (isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)'),
          marginBottom: '12px'
        }}
        onClick={() => handleProductSelection(product)}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.borderColor = themeColor;
            e.currentTarget.style.boxShadow = `0 4px 12px ${themeColor}40`;
            e.currentTarget.style.transform = 'translateY(-2px)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.borderColor = isDarkMode ? '#4a5568' : '#e9ecef';
            e.currentTarget.style.boxShadow = isDarkMode ? '0 2px 8px rgba(0, 0, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.05)';
            e.currentTarget.style.transform = 'translateY(0)';
          }
        }}
      >
        <div className="d-flex justify-content-between align-items-center">
          {/* Product Icon */}
          <div
            style={{
              width: '40px',
              height: '40px',
              background: isSelected
                ? themeColor
                : (isDarkMode ? '#4a5568' : '#f8f9fa'),
              borderRadius: '8px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginRight: '12px',
              border: `1px solid ${isDarkMode ? '#718096' : '#e9ecef'}`,
              overflow: 'hidden'
            }}
          >
            {product.menuItem_Image ? (
              <LazyLoadImage
                src={product.menuItem_Image}
                alt={product.menuItem_Name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  borderRadius: '6px',
                }}
              />
            ) : (
              <i className="fa fa-box" style={{
                fontSize: '16px',
                color: isSelected ? '#ffffff' : (isDarkMode ? '#a0aec0' : '#6c757d')
              }}></i>
            )}
          </div>

          {/* Product Info */}
          <div className="flex-grow-1">
            <span
              style={{
                fontSize: '14px',
                fontWeight: '600',
                color: isSelected
                  ? themeColor
                  : (isDarkMode ? '#e2e8f0' : '#495057'),
                display: 'block',
                marginBottom: '2px'
              }}
            >
              {product.menuItem_Name}
            </span>
            {product.menuItem_Type && (
              <small
                style={{
                  color: isSelected
                    ? themeColor
                    : (isDarkMode ? '#a0aec0' : '#6c757d'),
                  fontSize: '11px',
                  lineHeight: '1.3'
                }}
              >
                {product.menuItem_Type}
              </small>
            )}
          </div>
        </div>

        {/* Custom Checkbox */}
        <div
          style={{
            position: 'absolute',
            top: '12px',
            right: '12px',
            width: '20px',
            height: '20px',
            borderRadius: '4px',
            border: isSelected
              ? `2px solid ${themeColor}`
              : `2px solid ${isDarkMode ? '#718096' : '#e9ecef'}`,
            background: isSelected ? themeColor : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'all 0.2s ease'
          }}
        >
          {isSelected && (
            <i
              className="fa fa-check"
              style={{
                fontSize: '10px',
                color: 'white'
              }}
            ></i>
          )}
        </div>
      </div>
    );
  };

  // Simple test modal to check if modal opens
  if (!isOpen) return null;

 
  return (
    <CustomModal
      isOpen={isOpen}
      title="Select Products for Supplier"
      onClose={() => setShowModal(false)}
      onSubmit={handleSubmit}
      showButton={true}
      showSubmitButton={true}
      isCentered={true}
      submitButtonText={`Select Products (${tempSelectedProducts.length})`}
      themeColor={themeColor}
    >
      {/* Modal Content */}
      <p style={{
        color: isDarkMode ? '#a0aec0' : '#6c757d',
        marginBottom: '20px'
      }}>
 
      </p>

      {/* Search Input */}
      <div className="mb-4">
        <div
          style={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <input
            type="text"
            className="form-control"
            placeholder="Search products..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '12px 16px',
              border: `2px solid ${isDarkMode ? '#4a5568' : '#e9ecef'}`,
              borderRadius: '8px',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              backgroundColor: isDarkMode ? '#2d3748' : '#ffffff',
              color: isDarkMode ? '#e2e8f0' : '#495057'
            }}
            onFocus={(e) => {
              e.target.style.borderColor = themeColor;
              e.target.style.boxShadow = `0 0 0 3px ${themeColor}20`;
            }}
            onBlur={(e) => {
              e.target.style.borderColor = isDarkMode ? '#4a5568' : '#e9ecef';
              e.target.style.boxShadow = 'none';
            }}
          />
          <i
            className="fa fa-search"
            style={{
              position: 'absolute',
              right: '16px',
              color: isDarkMode ? '#a0aec0' : '#6c757d',
              fontSize: '14px'
            }}
          ></i>
          {searchTerm && (
            <button
              onClick={() => setSearchTerm("")}
              style={{
                position: 'absolute',
                right: '40px',
                background: 'transparent',
                border: 'none',
                color: isDarkMode ? '#a0aec0' : '#6c757d',
                cursor: 'pointer',
                padding: '4px',
                borderRadius: '4px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isDarkMode ? '#4a5568' : '#f8f9fa';
                e.currentTarget.style.color = isDarkMode ? '#e2e8f0' : '#495057';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
                e.currentTarget.style.color = isDarkMode ? '#a0aec0' : '#6c757d';
              }}
            >
              <i className="fa fa-times" style={{ fontSize: '12px' }}></i>
            </button>
          )}
        </div>
      </div>

      {/* Selected Count */}
      {tempSelectedProducts.length > 0 && (
        <div className="mb-3">
          <div
            style={{
              padding: '8px 12px',
              background: `${themeColor}20`,
              borderRadius: '6px',
              border: `1px solid ${themeColor}40`,
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <i className="fa fa-check-circle" style={{ color: themeColor }}></i>
            <span style={{
              fontSize: '14px',
              fontWeight: '500',
              color: themeColor
            }}>
              {tempSelectedProducts.length} product{tempSelectedProducts.length !== 1 ? 's' : ''} selected
            </span>
          </div>
        </div>
      )}

      {/* Product Cards */}
      <div style={{ height: "450px", overflowY: "auto", paddingRight: '8px' }}>
        {isLoading ? (
          <div className="text-center py-5">
            <i className="fa fa-spinner fa-spin me-2"></i>
            Loading products...
          </div>
        ) : filteredProducts && filteredProducts.length > 0 ? (
          <div>
            {filteredProducts.map((product) => (
              <RenderProductCard key={product.menuItem_ID} product={product} />
            ))}
          </div>
        ) : (
          <div
            className="text-center py-5"
            style={{
              background: isDarkMode ? '#2d3748' : '#f8f9fa',
              borderRadius: '12px',
              border: `2px dashed ${isDarkMode ? '#4a5568' : '#e9ecef'}`
            }}
          >
            <div
              style={{
                width: '60px',
                height: '60px',
                background: isDarkMode ? '#4a5568' : '#ffffff',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
                border: `1px solid ${isDarkMode ? '#718096' : '#e9ecef'}`
              }}
            >
              <i className="fa fa-box" style={{
                fontSize: '24px',
                color: isDarkMode ? '#a0aec0' : '#6c757d'
              }}></i>
            </div>
            <p style={{
              color: isDarkMode ? '#a0aec0' : '#6c757d',
              fontSize: '13px',
              marginBottom: '6px'
            }}>
              No products found
              {searchTerm.length > 0 ? ` matching "${searchTerm}"` : ""}
            </p>
            {searchTerm.length > 0 && (
              <small style={{
                color: isDarkMode ? '#a0aec0' : '#6c757d',
                fontSize: '11px'
              }}>
                Try adjusting your search terms
              </small>
            )}
          </div>
        )}
      </div>
    </CustomModal>
  );

};

export default SupplierProductSelectionModal;